import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Loader2,
  Stethoscope,
  CreditCard,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_CASES = [
  {
    id: "case1",
    label: "Caso 1 — Cefalea leve (esperado: OTC + GPC IMSS-047-08)",
    badge: "Leve",
    tone: "leve",
    historia_clinica: {
      edad: 30,
      peso: 70,
      altura: 170,
      sexo: "femenino",
      alergias: [],
      enfermedades_cronicas: [],
      medicacion_actual: [],
      condiciones_especiales: [],
      habitos: { alcohol: "ocasional", tabaco: "no", ejercicio: "moderado" },
      sintomas:
        "Adulta de 30 años con cefalea leve de tipo tensional de 1 día de evolución, sin fiebre, sin náuseas, sin déficits neurológicos. Trabajo frente a computadora. Descansó mal anoche.",
    },
  },
  {
    id: "case2",
    label: "Caso 2 — Contraindicación (esperado: NO AINEs + explicar)",
    badge: "Contraindicación",
    tone: "contra",
    historia_clinica: {
      edad: 55,
      peso: 82,
      altura: 172,
      sexo: "masculino",
      alergias: ["ibuprofeno"],
      enfermedades_cronicas: ["hipertensión arterial descontrolada"],
      medicacion_actual: ["losartán 50 mg c/24h (adherencia irregular)"],
      condiciones_especiales: [],
      habitos: { alcohol: "social", tabaco: "no" },
      sintomas:
        "Adulto de 55 años con dolor lumbar leve mecánico de 2 días tras cargar bultos, sin irradiación, sin déficit sensitivo ni motor, sin fiebre.",
    },
  },
  {
    id: "case3",
    label: "Caso 3 — Bandera roja (esperado: sin OTC + derivar)",
    badge: "Bandera roja",
    tone: "roja",
    historia_clinica: {
      edad: 42,
      peso: 75,
      altura: 168,
      sexo: "femenino",
      alergias: [],
      enfermedades_cronicas: [],
      medicacion_actual: [],
      condiciones_especiales: [],
      habitos: { alcohol: "no", tabaco: "no" },
      sintomas:
        "Adulta de 42 años con fiebre de 39.5°C sostenida por 6 días, sangrado gingival espontáneo, petequias en extremidades inferiores, dolor abdominal moderado y astenia marcada.",
    },
  },
];

const ToneBadge = ({ tone, label }) => {
  const map = {
    leve: "bg-emerald-50 text-emerald-700 border-emerald-200",
    contra: "bg-amber-50 text-amber-700 border-amber-200",
    roja: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
        map[tone] || ""
      }`}
    >
      {tone === "roja" ? <AlertTriangle size={12} /> : null}
      {label}
    </span>
  );
};

const ConsultaPanel = () => {
  const [selected, setSelected] = useState(PRESET_CASES[0].id);
  const [sintomas, setSintomas] = useState(PRESET_CASES[0].historia_clinica.sintomas);
  const [loading, setLoading] = useState(false);
  const [respuesta, setRespuesta] = useState(null);
  const [providerUsed, setProviderUsed] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [error, setError] = useState(null);

  const activeCase = useMemo(
    () => PRESET_CASES.find((c) => c.id === selected) || PRESET_CASES[0],
    [selected]
  );

  useEffect(() => {
    setSintomas(activeCase.historia_clinica.sintomas);
  }, [activeCase]);

  const runConsulta = async () => {
    setLoading(true);
    setError(null);
    setRespuesta(null);
    setProviderUsed(null);
    setAttempts([]);
    try {
      const payload = {
        historia_clinica: { ...activeCase.historia_clinica, sintomas },
      };
      const { data } = await axios.post(`${API}/poc/consulta`, payload, {
        timeout: 90_000,
      });
      setRespuesta(data.respuesta);
      setProviderUsed(data.provider_used);
      setAttempts(data.attempts || []);
      toast.success(`Respuesta generada vía ${data.provider_used}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setError(detail);
      toast.error("Error al consultar el motor clínico");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Stethoscope className="text-[#2563EB]" size={18} />
          <CardTitle className="text-lg">Motor clínico (Gemini)</CardTitle>
        </div>
        <CardDescription>
          Selecciona uno de los 3 casos de validación clínica. El backend construye el prompt con
          las reglas estrictas (OTC COFEPRIS V/VI, cruce de seguridad, banderas rojas, GPC MX) y
          llama a Gemini con la cadena de fallback configurada.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2">
          {PRESET_CASES.map((c) => (
            <label
              key={c.id}
              data-testid={`case-radio-${c.id}`}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                selected === c.id
                  ? "border-[#2563EB] bg-[#2563EB]/5"
                  : "border-border hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="case"
                className="mt-1 accent-[#2563EB]"
                checked={selected === c.id}
                onChange={() => setSelected(c.id)}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{c.label}</span>
                  <ToneBadge tone={c.tone} label={c.badge} />
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {c.historia_clinica.edad} años · {c.historia_clinica.sexo} · alergias:{" "}
                  {c.historia_clinica.alergias.length
                    ? c.historia_clinica.alergias.join(", ")
                    : "ninguna"}{" "}
                  · crónicas:{" "}
                  {c.historia_clinica.enfermedades_cronicas.length
                    ? c.historia_clinica.enfermedades_cronicas.join(", ")
                    : "ninguna"}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div>
          <div className="text-xs font-medium text-foreground mb-1">
            Cuadro clínico (editable)
          </div>
          <Textarea
            data-testid="sintomas-textarea"
            value={sintomas}
            onChange={(e) => setSintomas(e.target.value)}
            rows={4}
            className="text-sm"
          />
        </div>

        <Button
          data-testid="run-consulta-btn"
          onClick={runConsulta}
          disabled={loading || !sintomas.trim()}
          className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={16} /> Consultando Gemini…
            </>
          ) : (
            <>Ejecutar consulta clínica</>
          )}
        </Button>

        {providerUsed ? (
          <div className="text-xs text-muted-foreground" data-testid="provider-used">
            Ruta que respondió:{" "}
            <span className="font-mono text-[#0F2942]">{providerUsed}</span>
          </div>
        ) : null}

        {attempts && attempts.length > 0 ? (
          <div className="rounded-md bg-muted border border-border p-2 text-xs">
            <div className="text-foreground font-medium mb-1">Intentos (cadena de fallback):</div>
            <ul className="space-y-1">
              {attempts.map((a, i) => (
                <li key={i} className="flex items-start gap-2">
                  {a.status === "success" ? (
                    <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={14} />
                  ) : a.status === "skipped" ? (
                    <XCircle className="text-muted-foreground shrink-0 mt-0.5" size={14} />
                  ) : (
                    <XCircle className="text-red-500 shrink-0 mt-0.5" size={14} />
                  )}
                  <span>
                    <span className="font-mono">{a.provider}</span> — {a.status}
                    {a.error ? (
                      <span className="text-muted-foreground"> · {a.error}</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {error ? (
          <div
            data-testid="consulta-error"
            className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800"
          >
            {String(error)}
          </div>
        ) : null}

        {respuesta ? (
          <div
            data-testid="consulta-respuesta"
            className="rounded-md bg-white border border-border p-4 text-sm text-foreground whitespace-pre-wrap leading-relaxed"
          >
            {respuesta}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const PaymentsPanel = () => {
  const [loading, setLoading] = useState(null);
  const [sessions, setSessions] = useState([]);

  const openCheckout = async (kind) => {
    setLoading(kind);
    try {
      const origin_url = window.location.origin;
      let resp;
      if (kind === "consulta") {
        resp = await axios.post(`${API}/poc/checkout/consulta`, { origin_url });
      } else {
        resp = await axios.post(`${API}/poc/checkout/suscripcion`, {
          plan: kind,
          origin_url,
        });
      }
      const { url, session_id } = resp.data;
      setSessions((prev) => [{ label: kind, url, session_id }, ...prev].slice(0, 5));
      toast.success("Sesión de Stripe creada. Abriendo checkout…");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Error creando sesión: ${detail}`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard className="text-[#0F2942]" size={18} />
          <CardTitle className="text-lg">Pagos Stripe (MXN · test)</CardTitle>
        </div>
        <CardDescription>
          Sesiones de Checkout creadas con tu clave de prueba. Se abrirá una nueva pestaña. Tarjeta
          de prueba: <span className="font-mono">4242 4242 4242 4242</span>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <Button
            data-testid="pay-consulta-btn"
            onClick={() => openCheckout("consulta")}
            disabled={loading !== null}
            className="bg-[#0F2942] hover:bg-[#123553] text-white h-auto py-4 flex-col items-start gap-1 whitespace-normal text-left"
          >
            <span className="text-xs opacity-80">Pago único</span>
            <span className="font-semibold">Pagar consulta $39 MXN</span>
            {loading === "consulta" ? (
              <Loader2 className="animate-spin" size={14} />
            ) : null}
          </Button>
          <Button
            data-testid="pay-mensual-btn"
            onClick={() => openCheckout("mensual")}
            disabled={loading !== null}
            variant="outline"
            className="h-auto py-4 flex-col items-start gap-1 whitespace-normal text-left"
          >
            <span className="text-xs opacity-80">Suscripción</span>
            <span className="font-semibold">Plan mensual $69 MXN/mes</span>
            {loading === "mensual" ? <Loader2 className="animate-spin" size={14} /> : null}
          </Button>
          <Button
            data-testid="pay-anual-btn"
            onClick={() => openCheckout("anual")}
            disabled={loading !== null}
            variant="outline"
            className="h-auto py-4 flex-col items-start gap-1 whitespace-normal text-left"
          >
            <span className="text-xs opacity-80">Suscripción</span>
            <span className="font-semibold">Plan anual $699 MXN/año</span>
            {loading === "anual" ? <Loader2 className="animate-spin" size={14} /> : null}
          </Button>
        </div>

        {sessions.length > 0 ? (
          <div className="rounded-md border border-border p-3 text-xs">
            <div className="font-medium text-foreground mb-2">Últimas sesiones creadas</div>
            <ul className="space-y-2">
              {sessions.map((s, i) => (
                <li key={s.session_id + i} className="flex items-start gap-2">
                  <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={14} />
                  <div className="flex-1">
                    <div>
                      <span className="text-muted-foreground">{s.label}</span> ·{" "}
                      <span className="font-mono">{s.session_id}</span>
                    </div>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#2563EB] underline break-all"
                      data-testid={`session-link-${i}`}
                    >
                      {s.url}
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const CheckoutStatusBanner = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    let cancel = false;
    let attempts = 0;
    const poll = async () => {
      if (!sessionId) return;
      setPolling(true);
      while (!cancel && attempts < 10) {
        try {
          const { data } = await axios.get(`${API}/poc/checkout/status/${sessionId}`);
          setStatus(data);
          if (data.payment_status === "paid" || data.status === "complete") break;
        } catch (e) {
          // ignore, retry
        }
        attempts += 1;
        await new Promise((r) => setTimeout(r, 1500));
      }
      setPolling(false);
    };
    poll();
    return () => {
      cancel = true;
    };
  }, [sessionId]);

  if (!sessionId && !cancelled) return null;

  if (cancelled) {
    return (
      <div
        data-testid="checkout-cancelled-banner"
        className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-3 text-sm flex items-center justify-between"
      >
        <span>Pago cancelado. Puedes intentarlo de nuevo cuando quieras.</span>
        <Button variant="ghost" size="sm" onClick={() => navigate("/poc", { replace: true })}>
          Cerrar
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="checkout-status-banner"
      className={`rounded-md border p-3 text-sm flex items-center justify-between ${
        status?.payment_status === "paid"
          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
          : "border-border bg-muted text-foreground"
      }`}
    >
      <div>
        <div className="font-medium">
          {status?.payment_status === "paid"
            ? "Pago confirmado por Stripe"
            : polling
            ? "Verificando estado del pago…"
            : "Estado del pago"}
        </div>
        <div className="text-xs opacity-80 mt-0.5">
          <span className="font-mono">{sessionId}</span>
          {status ? (
            <>
              {" · "}status: <span className="font-mono">{status.status}</span>
              {" · "}pago: <span className="font-mono">{status.payment_status}</span>
            </>
          ) : null}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={() => navigate("/poc", { replace: true })}>
        Cerrar
      </Button>
    </div>
  );
};

export default function Poc() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-8 space-y-6">
        <div>
          <h1
            className="font-display text-3xl font-semibold text-[#0F2942] tracking-tight"
            data-testid="page-title"
          >
            Fase 0 — POC de riesgo
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Prueba integrada del motor clínico Gemini + Stripe Checkout MXN. Sin autenticación en
            esta página (POC interno).
          </p>
        </div>

        <CheckoutStatusBanner />

        <div className="grid lg:grid-cols-2 gap-6">
          <ConsultaPanel />
          <PaymentsPanel />
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}
